// Loading a layout into the Custom HTML/CSS block from files on disk.
//
// Authors do not write these layouts in this dialog. They write them in an
// editor, or ask an assistant for them, and the result is a `.html` file and
// often a `.css` file beside it. Getting those in meant opening each one,
// selecting all, copying, finding the right box and pasting — twice, and with
// the standing risk of pasting the CSS into the HTML box.
//
// So the two boxes can be filled from files directly. Three shapes arrive:
//
//   1. **A file this app exported.** `buildCustomHtmlLayoutFile` fences the
//      author's own HTML and CSS inside comment markers, precisely so they can
//      be found again. Honouring them is what makes the round trip exact —
//      without it the preview scaffolding written around the layout (the page
//      background, the 16:9 stage) would be imported as if the author had
//      written it.
//   2. **A whole HTML document** from somewhere else. Its `<style>` blocks
//      belong in the CSS box — the same fold the editor already performs on a
//      pasted `<style>` — and its `<body>` is the markup, since `<html>`,
//      `<head>` and `<body>` are not things a slide layout may contain.
//   3. **A fragment**, which is used as it stands.
//
// Nothing here sanitizes. That is deliberate: typed markup is stored raw and
// cleaned on the way to the DOM (`sanitizeSlideLayoutHtml`) and on the way into
// an export, so imported markup taking the identical path is what guarantees
// there is no route in here that typing does not already have. Cleaning at
// import instead would put a *second*, differently-behaved gate in front of
// one of the two doors — and would show the author markup in the box that is
// not the markup their file contains.

import { extractStyleBlocks } from './customCss';
import { LAYOUT_FILE_MARKERS } from './customHtmlLayoutFile';

/** What the picker offers, and what the routing understands. */
export const IMPORT_ACCEPT = '.html,.htm,.css,text/html,text/css';

export const HTML_EXTENSIONS = ['.html', '.htm'];
export const CSS_EXTENSIONS = ['.css'];

/** Reject anything past this: these are two text boxes, not a file store. */
export const MAX_IMPORT_BYTES = 2 * 1024 * 1024;

const extensionOf = (name = '') => {
  const match = /\.[a-z0-9]+$/i.exec(String(name).trim());
  return match ? match[0].toLowerCase() : '';
};

/**
 * Which box a file belongs in.
 *
 * The extension decides. MIME type is only consulted for a file that has no
 * extension at all, because browsers are unreliable here in both directions:
 * a `.css` picked from some Windows configurations arrives as `text/plain`,
 * and a `.html` can arrive with an empty type. Trusting the type over the
 * extension would refuse files that are plainly fine.
 *
 * @returns {'html'|'css'|null} null for anything that is neither
 */
export const classifyImportFile = (file) => {
  const extension = extensionOf(file?.name);
  if (HTML_EXTENSIONS.includes(extension)) return 'html';
  if (CSS_EXTENSIONS.includes(extension)) return 'css';
  if (extension) return null;

  const type = String(file?.type || '').toLowerCase();
  if (type === 'text/html') return 'html';
  if (type === 'text/css') return 'css';
  return null;
};

/** The region between two marker comments, or null when they are not there. */
const between = (text, startMarker, endMarker) => {
  const start = text.indexOf(startMarker);
  if (start === -1) return null;
  const afterStart = text.indexOf('*/', start);
  const commentEnd = text.indexOf('-->', start);
  // The CSS markers sit in `/* … */`, the HTML markers in `<!-- … -->`.
  const openEnd = afterStart !== -1 && (commentEnd === -1 || afterStart < commentEnd)
    ? afterStart + 2
    : commentEnd + 3;
  const end = text.indexOf(endMarker, openEnd);
  if (end === -1) return null;
  // Back up over the comment opener that introduces the closing marker.
  const closeStart = Math.max(
    text.lastIndexOf('/*', end),
    text.lastIndexOf('<!--', end)
  );
  return text.slice(openEnd, closeStart > openEnd ? closeStart : end);
};

/** Strip the uniform indentation the exporter added, and the empty-box notes. */
const undent = (text) => {
  const lines = String(text || '').replace(/^\n+|\s+$/g, '').split('\n');
  const widths = lines
    .filter((line) => line.trim())
    .map((line) => line.match(/^\s*/)[0].length);
  const pad = widths.length ? Math.min(...widths) : 0;
  return lines.map((line) => line.slice(pad)).join('\n').trim();
};

const EMPTY_BOX_NOTES = ['/* (the CSS box is empty) */', '<!-- (the HTML box is empty) -->'];
const dropEmptyNote = (text) => (EMPTY_BOX_NOTES.includes(text.trim()) ? '' : text);

/**
 * Split an imported HTML file into the two boxes.
 *
 * @param {string} source - the file's text
 * @returns {{ html: string, css: string }}
 */
export const parseImportedHtml = (source = '') => {
  const text = typeof source === 'string' ? source : '';
  if (!text.trim()) return { html: '', css: '' };

  // 1. A file this app wrote: take exactly the two marked regions.
  const markedHtml = between(text, LAYOUT_FILE_MARKERS.htmlStart, LAYOUT_FILE_MARKERS.htmlEnd);
  const markedCss = between(text, LAYOUT_FILE_MARKERS.cssStart, LAYOUT_FILE_MARKERS.cssEnd);
  if (markedHtml !== null || markedCss !== null) {
    return {
      html: dropEmptyNote(undent(markedHtml || '')),
      css: dropEmptyNote(undent(markedCss || ''))
    };
  }

  // 2 & 3. Fold out any <style>, then keep the body if this is a whole page.
  const { html: withoutStyles, css } = extractStyleBlocks(text);
  const body = /<body[^>]*>([\s\S]*?)<\/body>/i.exec(withoutStyles);
  const markup = body
    ? body[1]
    // A document with no <body> tag can still carry a <head> and a doctype.
    : withoutStyles
      .replace(/<!doctype[^>]*>/gi, '')
      .replace(/<\/?html[^>]*>/gi, '')
      .replace(/<head[\s\S]*?<\/head>/gi, '');

  return { html: undent(markup), css: undent(css) };
};

/** CSS files go in as they are; only the leading and trailing blank lines go. */
export const parseImportedCss = (source = '') =>
  (typeof source === 'string' ? source : '').replace(/^\s*\n|\s+$/g, '');

const readAsText = (file) => new Promise((resolve, reject) => {
  if (typeof FileReader === 'undefined') { reject(new Error('this browser cannot read files')); return; }
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(new Error(`${file.name} could not be read`));
  reader.readAsText(file);
});

const describeUnsupported = (file) => {
  const extension = extensionOf(file?.name);
  if (extension === '.js' || extension === '.mjs' || extension === '.ts') {
    return `${file.name}: JavaScript files are not supported here. Import an HTML or CSS file — a layout is markup and styles, and scripts are removed from it anyway.`;
  }
  if (extension === '.json') {
    return `${file.name}: that is a deck file, not a layout. Use Open ▸ Open file for a deck, or import an HTML or CSS file here.`;
  }
  return `${file.name || 'That file'}: only .html, .htm and .css files can be imported here.`;
};

/**
 * Read a set of picked files into the two boxes.
 *
 * Never throws and never partially applies: it returns what the boxes *would*
 * become, and the caller decides whether that needs confirming first.
 *
 * @param {Array<File>} files
 * @returns {Promise<{ html: string|null, css: string|null, sources: object, errors: string[] }>}
 *   `html` / `css` are null when no file addressed that box, which is how the
 *   caller tells "import an empty file" from "do not touch this box".
 */
export const readImportedFiles = async (files = []) => {
  const list = Array.from(files || []).filter(Boolean);
  const errors = [];
  const sources = {};
  let html = null;
  let css = null;

  if (!list.length) return { html, css, sources, errors };

  for (const file of list) {
    const kind = classifyImportFile(file);
    if (!kind) {
      errors.push(describeUnsupported(file));
      continue;
    }
    if (Number(file.size) > MAX_IMPORT_BYTES) {
      errors.push(`${file.name} is larger than ${Math.round(MAX_IMPORT_BYTES / 1024 / 1024)} MB. A slide layout that big will not render.`);
      continue;
    }

    // Two files aimed at the same box is an ambiguity, not a merge: nobody
    // means "run these two unrelated layouts together", and picking one
    // silently would lose the other without saying so.
    if ((kind === 'html' && html !== null) || (kind === 'css' && css !== null)) {
      errors.push(`More than one ${kind.toUpperCase()} file was chosen (${sources[kind]} and ${file.name}). Import one at a time so it is clear which one lands in the box.`);
      continue;
    }

    let text;
    try {
      text = await readAsText(file);
    } catch (error) {
      errors.push(error.message);
      continue;
    }

    if (kind === 'html') {
      const parsed = parseImportedHtml(text);
      html = parsed.html;
      sources.html = file.name;
      // A `<style>` inside the imported HTML belongs in the CSS box — the same
      // fold a pasted `<style>` already gets. It must not overwrite a CSS file
      // chosen in the same go, which is the more explicit statement of intent.
      if (parsed.css && css === null) {
        css = parsed.css;
        sources.css = `${file.name} (its <style> block)`;
      }
    } else {
      css = parseImportedCss(text);
      sources.css = file.name;
    }
  }

  // A CSS file chosen alongside an HTML file wins over that file's <style>.
  const cssFile = list.find((file) => classifyImportFile(file) === 'css');
  if (cssFile && sources.css && sources.css.includes('<style>')) {
    try {
      css = parseImportedCss(await readAsText(cssFile));
      sources.css = cssFile.name;
    } catch (error) {
      errors.push(error.message);
    }
  }

  return { html, css, sources, errors };
};

/**
 * Which boxes an import would overwrite, given what they hold now.
 *
 * Only a box that has something in it and is about to be replaced counts:
 * filling an empty box destroys nothing and must not stop to ask.
 *
 * @returns {Array<'HTML'|'CSS'>}
 */
export const importWouldReplace = ({ incoming = {}, current = {} } = {}) => {
  const replaced = [];
  if (incoming.html !== null && incoming.html !== undefined && String(current.html || '').trim()) replaced.push('HTML');
  if (incoming.css !== null && incoming.css !== undefined && String(current.css || '').trim()) replaced.push('CSS');
  return replaced;
};
